import type { HttpClient } from '@wix/http-client';
import type { ReportError } from '@wix/editor-content-injector';
import SgThemeFilteringService from '../sgThemeFilteringService/sgThemeFilteringService';
import {
  ChatMessageRole,
  ChatTopic,
  InitialPromptActions,
  UnrelatedAction,
} from './constants';
import { ParsedInitialResponse, ParsedRelatedActionResponse } from './types';
import {
  getInitialResponseFromPromptHub,
  getThemeSuggestionPromptHub,
  getEnglishTranslatedUserInput,
  getActionFromUnrelatedPromptHub,
  getNewExtendedKitForShuffleColors,
  getRelatedUserMessage,
  getRelatedPromptAnswerPromptHubHidden,
  getMatchingProfileIdPromptHub,
} from './utils';
import { ErrorReporter } from '@wix/editor-error-reporter';
import {
  KitDefinition,
  KitExtendedDescription,
  KitExtendedDescriptionKey,
  KitInjectionOptions,
} from '@wix/editor-kits';
import { ChatControllerBiEventNumbers } from '../constants';
import _ from 'lodash';
import { ChatMessage, Library, TopicValue } from '../types';
import { LayoutFamilyDefinition } from '../../types';

export class SgChat {
  client: HttpClient;
  reportDebug: ReportError;
  sgThemeFiltering: SgThemeFilteringService;

  constructor(
    httpClient: HttpClient,
    reportDebug: ReportError,
    sgThemeFiltering: SgThemeFilteringService,
  ) {
    this.client = httpClient;
    this.reportDebug = reportDebug;
    this.sgThemeFiltering = sgThemeFiltering;
  }

  private async filterTopThemes(
    userInput: string,
    kitExtendedDescriptions: {
      [key in KitExtendedDescriptionKey]: KitExtendedDescription;
    },
    sendSiteCommonBi: (
      evid: ChatControllerBiEventNumbers,
      fields: NonNullable<unknown>,
    ) => void,
  ) {
    try {
      ErrorReporter.breadcrumb('sgChat.filterTopThemes request', {
        userInput,
      });
      const res = await this.sgThemeFiltering.filterKitsByUserInput(
        userInput,
        kitExtendedDescriptions,
      );

      if (!res.output?.values?.top_themes?.strVal) {
        sendSiteCommonBi(ChatControllerBiEventNumbers.SG_ERROR, {
          flow: 'SG',
          topic: ChatTopic.ThemeRecommendation,
          errn: 'top themes are null or empty',
          functionName: 'filterTopThemes',
        });
        return _.sampleSize(kitExtendedDescriptions, 5);
      }
      const topThemes = JSON.parse(res.output?.values?.top_themes?.strVal);

      ErrorReporter.breadcrumb('sgChat.filterTopThemes response', {
        topThemes,
      });
      if (!topThemes || topThemes?.length === 0) {
        sendSiteCommonBi(ChatControllerBiEventNumbers.SG_ERROR, {
          flow: 'SG',
          topic: ChatTopic.ThemeRecommendation,
          errn: 'top themes are null or empty',
          functionName: 'filterTopThemes',
        });
        return _.sampleSize(kitExtendedDescriptions, 5);
      }

      return topThemes;
    } catch (error) {
      let errorMessage;
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      sendSiteCommonBi(ChatControllerBiEventNumbers.SG_ERROR, {
        flow: 'SG',
        topic: ChatTopic.ThemeRecommendation,
        errn: errorMessage,
        functionName: 'filterTopThemes',
      });
      ErrorReporter.captureException(error, {
        tags: {
          siteGenerationWizard: true,
          siteGenerationAiErrorResponse: true,
        },
        extra: {
          userInput,
        },
      });
      return _.sampleSize(kitExtendedDescriptions, 5);
    }
  }

  private async getInitialMiniChatAction(
    chatTopic: ChatTopic.Layout | ChatTopic.Theme | ChatTopic.MatchImages,
    initialUserMessage: ChatMessage,
    businessType: string,
    library: Library,
    kitExtendedDescriptions: {
      [key in KitExtendedDescriptionKey]: KitExtendedDescription;
    },
    userInput: string,
    sendSiteCommonBi: (
      evid: ChatControllerBiEventNumbers,
      fields: NonNullable<unknown>,
    ) => void,
  ): Promise<ParsedInitialResponse> {
    const parsedResponse = await getInitialResponseFromPromptHub(
      chatTopic,
      initialUserMessage,
      businessType,
      library,
      userInput,
      this.client,
      this.reportDebug,
      sendSiteCommonBi,
    );

    if (parsedResponse.action === InitialPromptActions.CHANGE_THEME) {
      const translatedUserInput = await getEnglishTranslatedUserInput(
        userInput,
        chatTopic,
        this.client,
        sendSiteCommonBi,
      );
      const response: KitExtendedDescriptionKey =
        await getThemeSuggestionPromptHub(
          translatedUserInput,
          businessType,
          kitExtendedDescriptions,
          initialUserMessage,
          this.filterTopThemes.bind(this),
          this.client,
          this.reportDebug,
          sendSiteCommonBi,
        );
      parsedResponse.suggestion = response;
    }

    return parsedResponse;
  }

  async sendMessage(
    userInput: string,
    initialUserMessage: ChatMessage,
    chatTopic: ChatTopic.Layout | ChatTopic.Theme,
    messagesHistory: ChatMessage[],
    library: Library,
    kitExtendedDescriptions: {
      [key in KitExtendedDescriptionKey]: KitExtendedDescription;
    },
    businessType: string,
    topicValue: TopicValue,
    setChatHistory: (chatTopic: ChatTopic, message: ChatMessage) => void,
    currentGenerated: {
      currentKit: KitDefinition;
      previousKit: KitDefinition | undefined;
      currentLayoutFamily: LayoutFamilyDefinition;
      currentKitInjectionOption: KitInjectionOptions;
      previousKitInjectionOption: KitInjectionOptions | undefined;
      isColorFromLogoNeeded: boolean;
    },
    languageCode: string,
    sendSiteCommonBi: (
      evid: ChatControllerBiEventNumbers,
      fields: NonNullable<unknown>,
    ) => void,
  ): Promise<ParsedRelatedActionResponse> {
    const { action, suggestion } = await this.getInitialMiniChatAction(
      chatTopic,
      initialUserMessage,
      businessType,
      library,
      kitExtendedDescriptions,
      userInput,
      sendSiteCommonBi,
    );
    let unrelatedAction: UnrelatedAction | undefined;
    if (action === InitialPromptActions.UNRELATED) {
      unrelatedAction = await getActionFromUnrelatedPromptHub(
        chatTopic,
        userInput,
        this.client,
        sendSiteCommonBi,
      );
    }

    let newExtendetKitSuggestion: KitExtendedDescription | undefined;
    let topicDescriptionIndex =
      suggestion && suggestion !== 'NA' ? suggestion : topicValue.current;
    if (action === InitialPromptActions.SHUFFLE_COLORS) {
      newExtendetKitSuggestion = getNewExtendedKitForShuffleColors(
        kitExtendedDescriptions,
        currentGenerated,
      );
      topicDescriptionIndex = `${newExtendetKitSuggestion.kitName}_${newExtendetKitSuggestion.preset}`;
    }

    const contextDescription =
      chatTopic === ChatTopic.Theme
        ? kitExtendedDescriptions[
            topicDescriptionIndex as KitExtendedDescriptionKey
          ]?.description
        : library[topicDescriptionIndex]?.description;

    const relatedUserMessage = {
      role: ChatMessageRole.User,
      content: getRelatedUserMessage(
        chatTopic,
        userInput,
        unrelatedAction ? [unrelatedAction] : [action],
        action === InitialPromptActions.SHUFFLE_COLORS &&
          newExtendetKitSuggestion
          ? newExtendetKitSuggestion.description
          : contextDescription,
      ),
    };
    setChatHistory(chatTopic, relatedUserMessage);

    const promptAnswer = await getRelatedPromptAnswerPromptHubHidden(
      chatTopic,
      relatedUserMessage,
      messagesHistory,
      this.client,
      sendSiteCommonBi,
      languageCode,
    );

    const newResponseMessageRecord = {
      role: ChatMessageRole.Assistant,
      content: promptAnswer,
    };
    setChatHistory(chatTopic, newResponseMessageRecord);

    return {
      content: promptAnswer,
      suggestion,
      action,
      isValidResponse: true,
      contextDescription,
      newExtendedKit: newExtendetKitSuggestion,
    };
  }

  async getMatchingProfileId(
    businessType: string,
    description: string,
    imageLibrary: Library,
    sendSiteCommonBi: (
      evid: ChatControllerBiEventNumbers,
      fields: NonNullable<unknown>,
    ) => void,
  ): Promise<string | null> {
    return getMatchingProfileIdPromptHub(
      businessType,
      description,
      imageLibrary,
      this.reportDebug,
      this.client,
      sendSiteCommonBi,
    );
  }
}
